/**
 * LABORATÓRIO — a bancada de quem escreve e escolhe item.
 *
 * O mesmo motor, o mesmo player e a mesma correção do percurso, com tudo o que o aluno
 * não vê: a habilidade da matriz, o task-model, o envelope de dificuldade, a seed, a
 * galeria de variantes e o comentário detalhado de cada resposta.
 *
 * E uma ponte para o percurso: "Adicionar ao percurso" leva a variante que está na
 * tela — com a seed fixada — para a lista de questões do aluno.
 */

import { formatBR } from '../core/num.ts';
import { seed as comoSeed } from '../core/rng.ts';
import { renderGallery } from '../dev/gallery.ts';
import { generate } from '../engine/generate.ts';
import { getTemplate, REGISTRY, type AnyTemplate } from '../engine/registry.ts';
import type { InteractivityLevel, ItemInstance } from '../schema/item.ts';
import { AXIS_LABEL, COMPETENCY_LABEL } from '../schema/taxonomy.ts';
import { createPlayer, type ItemPlayer } from '../ui/player.ts';
import { adicionarAoPercurso } from './config.ts';
import { habilidadeDe, NIVEL_TXT } from './itemMeta.ts';
import type { ContextoApp, Vista } from './tipos.ts';

const NIVEIS: readonly InteractivityLevel[] = ['N0', 'N1', 'N2', 'N3', 'N4'];

export interface VistaLaboratorio extends Vista {
  abrirItem(templateId: string, seed?: string): void;
}

export function criarVistaLaboratorio(ctx: ContextoApp): VistaLaboratorio {
  const root = document.createElement('section');
  root.className = 'lab';

  // --- lista de itens, por nível --------------------------------------------
  const lista = document.createElement('nav');
  lista.className = 'lab-lista';
  lista.setAttribute('aria-label', 'Itens do banco');

  const botoes = new Map<string, HTMLButtonElement>();
  for (const nivel of NIVEIS) {
    const doNivel = REGISTRY.filter((t) => t.level === nivel);
    if (doNivel.length === 0) continue;
    const grupo = document.createElement('div');
    grupo.className = 'lab-grupo';
    const titulo = document.createElement('p');
    titulo.className = 'lab-grupo-titulo';
    titulo.textContent = `${nivel} · ${NIVEL_TXT[nivel]}`;
    grupo.appendChild(titulo);

    for (const t of doNivel) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'lab-item';
      const nome = document.createElement('span');
      nome.className = 'lab-item-titulo';
      nome.textContent = t.title;
      const sub = document.createElement('span');
      sub.className = 'lab-item-sub';
      sub.textContent = `${t.skills.join(', ')} · ${t.taskModel as string}`;
      b.append(nome, sub);
      b.addEventListener('click', () => {
        atual = t;
        modoGaleria = false;
        btnGaleria.textContent = 'Galeria';
        mostrarItem();
      });
      botoes.set(t.id as string, b);
      grupo.appendChild(b);
    }
    lista.appendChild(grupo);
  }

  // --- barra ------------------------------------------------------------------
  const principal = document.createElement('div');
  principal.className = 'lab-principal';

  const barra = document.createElement('div');
  barra.className = 'lab-barra';

  const caixaSeed = document.createElement('input');
  caixaSeed.type = 'text';
  caixaSeed.value = 's0';
  caixaSeed.setAttribute('aria-label', 'Seed');
  const campoSeed = document.createElement('label');
  campoSeed.className = 'field';
  campoSeed.append(document.createTextNode('Seed'), caixaSeed);

  const btnSortear = botao('Sortear variante', 'btn');
  const btnGaleria = botao('Galeria', 'btn');
  const btnAdicionar = botao('Adicionar ao percurso', 'btn btn-primary');
  btnAdicionar.title = 'Leva esta variante, com a seed fixada, para o fim do percurso';
  const btnTelemetria = botao('Baixar telemetria', 'btn');

  barra.append(campoSeed, btnSortear, btnGaleria, btnAdicionar, btnTelemetria);

  const ficha = document.createElement('div');
  ficha.className = 'lab-ficha';
  const conteudo = document.createElement('div');
  conteudo.className = 'lab-conteudo';

  principal.append(barra, ficha, conteudo);
  root.append(lista, principal);

  // --- estado -----------------------------------------------------------------
  let atual: AnyTemplate = REGISTRY[0] as AnyTemplate;
  let modoGaleria = false;
  let player: ItemPlayer | null = null;
  let desenhado = false;

  function mostrarItem(): void {
    desenhado = true;
    player?.dispose();
    player = null;
    conteudo.replaceChildren();

    for (const [id, b] of botoes)
      b.setAttribute('aria-current', String(id === (atual.id as string)));

    if (modoGaleria) {
      desenharFicha(null);
      btnAdicionar.disabled = true;
      // Clicar numa variante da galeria abre a questão com aquela seed — é assim que
      // se escolhem variantes à mão para o percurso.
      conteudo.appendChild(
        renderGallery(atual, 60, (s) => {
          caixaSeed.value = s as string;
          modoGaleria = false;
          btnGaleria.textContent = 'Galeria';
          mostrarItem();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }),
      );
      return;
    }

    const s = caixaSeed.value.trim() || 's0';
    const r = generate(atual, comoSeed(s));

    if (r.status === 'rejected') {
      desenharFicha(null);
      btnAdicionar.disabled = true;
      const aviso = document.createElement('p');
      aviso.className = 'notice';
      // Uma seed rejeitada NÃO vira seed+1 em silêncio: dizer que ela foi rejeitada, e
      // por quê, é o que mantém `generate` uma função da seed.
      aviso.textContent =
        `Seed rejeitada (${r.reason}): ${r.detail}. ` +
        'Isso é comportamento correto — tente outra seed.';
      conteudo.appendChild(aviso);
      return;
    }

    btnAdicionar.disabled = false;
    desenharFicha(r.instance);
    player = createPlayer(r.instance, {
      mode: 'practice',
      telemetry: ctx.telemetria,
      showRationale: true,
      // Aqui os metadados de autoria SÃO o assunto.
      showMeta: true,
    });
    conteudo.appendChild(player.element);
  }

  function desenharFicha(inst: ItemInstance | null): void {
    const h = document.createElement('h2');
    h.textContent = atual.title;

    const dl = document.createElement('dl');
    const linha = (rotulo: string, valor: string): void => {
      const dt = document.createElement('dt');
      dt.textContent = rotulo;
      const dd = document.createElement('dd');
      dd.textContent = valor;
      dl.append(dt, dd);
    };

    const hab = habilidadeDe(atual);
    linha(
      'Habilidade',
      hab ? `${hab.codigo as string} — ${hab.descricao}` : atual.skills.join(', '),
    );
    if (hab) {
      linha('Competência', `${hab.competencia} · ${COMPETENCY_LABEL[hab.competencia]}`);
      linha('Ano', `${hab.ano}º ano do Ensino Médio`);
    }
    linha('Eixo cognitivo', `${atual.axis} · ${AXIS_LABEL[atual.axis]}`);
    linha('Task-model', atual.taskModel as string);
    linha('Nível', `${atual.level} · ${NIVEL_TXT[atual.level]}`);
    linha('Modelo de resposta', atual.responseModel);
    linha(
      'Dificuldade provisória',
      inst
        ? `b̂ = ${formatBR(inst.difficulty.provisionalB, 2)} (envelope de ` +
            `${formatBR(inst.difficulty.lo, 2)} a ${formatBR(inst.difficulty.hi, 2)}; a priori)`
        : '—',
    );
    linha('Versão do gerador', `v${atual.version}`);

    ficha.replaceChildren(h, dl);
  }

  // --- eventos ----------------------------------------------------------------
  caixaSeed.addEventListener('change', mostrarItem);

  btnSortear.addEventListener('click', () => {
    caixaSeed.value = `s${Math.floor(Math.random() * 100000)}`;
    modoGaleria = false;
    btnGaleria.textContent = 'Galeria';
    mostrarItem();
  });

  btnGaleria.addEventListener('click', () => {
    modoGaleria = !modoGaleria;
    btnGaleria.textContent = modoGaleria ? 'Ver item' : 'Galeria';
    mostrarItem();
  });

  btnAdicionar.addEventListener('click', () => {
    const s = caixaSeed.value.trim() || 's0';
    adicionarAoPercurso(atual.id as string, s);
    ctx.avisar(`“${atual.title}” entrou no percurso, com a variante ${s}.`);
  });

  btnTelemetria.addEventListener('click', () => {
    const blob = new Blob([ctx.telemetria.toJSON()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `telemetria-${Date.now()}.json`;
    a.click();
    // Revogar no mesmo instante do clique cancela o download em alguns navegadores: o
    // endereço some antes de o arquivo começar a ser lido.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  });

  return {
    element: root,
    mostrar() {
      if (!desenhado) mostrarItem();
    },
    abrirItem(templateId: string, s?: string) {
      atual = getTemplate(templateId);
      if (s) caixaSeed.value = s;
      modoGaleria = false;
      btnGaleria.textContent = 'Galeria';
      mostrarItem();
    },
  };
}

function botao(texto: string, cls: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = cls;
  b.textContent = texto;
  return b;
}
